import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, Scissors, Home, Users } from "lucide-react";
import { toast } from "sonner";

export function ReminderBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const enabled = user?.role === "admin" || user?.role === "staff";
  const [items, setItems] = useState([]);
  const [dates, setDates] = useState({});
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    try { const { data } = await api.get("/service-reminders/pending"); setItems(data); } catch (e) { console.error("Failed to load pending reminders:", e); }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [enabled, load]);

  if (!enabled) return null;
  const count = items.length;

  const setReminder = async (it) => {
    const due = dates[it.record_id];
    if (!due) { toast.error("Pick a date for the next session"); return; }
    try {
      await api.post("/service-reminders", { record_type: it.record_type, record_id: it.record_id, due_date: due });
      toast.success("Reminder set");
      load();
    } catch (e) { toast.error("Could not set reminder"); }
  };
  const skip = async (it) => {
    try { await api.post(`/service-reminders/${it.record_type}/${it.record_id}/dismiss`); load(); } catch (e) { console.error("Failed to dismiss reminder (will retry on next poll):", e); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="reminder-bell" className="relative grid place-items-center h-9 w-9 rounded-xl hover:bg-secondary transition-colors">
          <Bell className={`h-4 w-4 ${count ? "text-accent" : "text-muted-foreground"}`} strokeWidth={1.75} />
          {count > 0 && (
            <span data-testid="reminder-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-accent text-accent-foreground text-[10px] font-bold animate-pulse">{count}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
          <div>
            <div className="font-primary font-semibold">Set next session</div>
            <p className="text-xs text-muted-foreground">{count ? `${count} booking(s) need a follow-up date.` : "All caught up — no pending reminders."}</p>
          </div>
          <Button size="sm" variant="outline" data-testid="bell-set-next-session" onClick={() => { setOpen(false); navigate("/clients?needs_session=1"); }}>
            <Users className="h-4 w-4 mr-1" />Clients
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {items.map((it) => {
            const Icon = it.record_type === "boarding" ? Home : Scissors;
            return (
              <div key={`${it.record_type}:${it.record_id}`} data-testid={`pending-reminder-${it.record_id}`} className="p-3">
                <div className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" />{it.pet_name} <span className="text-muted-foreground font-normal">· {it.kind}</span></div>
                <div className="text-xs text-muted-foreground mb-2">{it.owner_name} · {it.branch}</div>
                <div className="flex gap-2">
                  <Input type="date" className="h-9 flex-1" data-testid={`reminder-date-${it.record_id}`}
                         value={dates[it.record_id] || ""} onChange={(e) => setDates((s) => ({ ...s, [it.record_id]: e.target.value }))} />
                  <Button size="sm" onClick={() => setReminder(it)} data-testid={`set-reminder-${it.record_id}`}>Set</Button>
                  <Button size="sm" variant="ghost" onClick={() => skip(it)}>Skip</Button>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
